import { INCIDENT_KINDS, INCIDENT_NAMES, type IncidentKind } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '../sync/db.js';
import type { DraftIncident, ReportDraft } from './draft.js';

/**
 * Шаг «события смены»: запуск, выгонка, ремонт, обучение.
 *
 * Каждое — крупная кнопка-переключатель. Включили — раскрылись длительность
 * и комментарий. Ремонт без описания и обучение без обучаемого отправить нельзя.
 */

export interface IncidentsStepProps {
  draft: ReportDraft;
  siteCode: string;
  onChange(patch: Partial<ReportDraft>): void;
}

const DURATIONS = [10, 30, 60, 120];

export function IncidentsStep({ draft, siteCode, onChange }: IncidentsStepProps): JSX.Element {
  const people = useLiveQuery(
    async () => (await db.users.where('role').equals('OPERATOR').sortBy('fullName')),
    [],
    [],
  );

  // Запуск и выгонка бывают только на прессовании
  const kinds: readonly IncidentKind[] =
    siteCode === 'PRESSING' ? INCIDENT_KINDS : (['REPAIR', 'TRAINING'] as const);

  const find = (kind: IncidentKind): DraftIncident | undefined =>
    draft.incidents.find((incident) => incident.kind === kind);

  const toggle = (kind: IncidentKind): void => {
    const existing = find(kind);
    const incidents = existing
      ? draft.incidents.filter((incident) => incident.kind !== kind)
      : [...draft.incidents, { kind, durationMinutes: null, comment: null, traineeUserId: null }];
    onChange({ incidents });
  };

  const patch = (kind: IncidentKind, change: Partial<DraftIncident>): void => {
    onChange({
      incidents: draft.incidents.map((incident) =>
        incident.kind === kind ? { ...incident, ...change } : incident,
      ),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {kinds.map((kind) => {
        const incident = find(kind);
        const on = Boolean(incident);

        return (
          <section key={kind} className="rounded-xl bg-card shadow-card p-4">
            <button
              type="button"
              onClick={() => toggle(kind)}
              className={`flex w-full min-h-touch items-center justify-between rounded-xl px-4 py-4 text-lg font-semibold ${
                on ? 'bg-sky-700 text-white' : 'bg-muted'
              }`}
            >
              <span>{INCIDENT_NAMES[kind]}</span>
              <span>{on ? 'да' : 'нет'}</span>
            </button>

            {incident && (
              <div className="mt-4 flex flex-col gap-3">
                <div>
                  <p className="mb-2 text-base text-body">
                    Сколько заняло: {incident.durationMinutes ?? 0} мин
                  </p>
                  <div className="flex flex-wrap gap-touch">
                    {DURATIONS.map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() =>
                          patch(kind, {
                            durationMinutes: (incident.durationMinutes ?? 0) + minutes,
                          })
                        }
                        className="min-h-touch rounded-xl bg-muted px-5 text-lg font-semibold active:bg-line"
                      >
                        +{minutes}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => patch(kind, { durationMinutes: null })}
                      className="min-h-touch rounded-xl bg-canvas px-5 text-base font-semibold"
                    >
                      Сброс
                    </button>
                  </div>
                </div>

                <label className="flex flex-col gap-2 text-base">
                  {kind === 'REPAIR' ? 'Что ремонтировали (обязательно)' : 'Комментарий'}
                  <textarea
                    rows={2}
                    value={incident.comment ?? ''}
                    onChange={(event) => patch(kind, { comment: event.target.value || null })}
                    className="rounded-xl bg-canvas px-4 py-3 text-lg outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </label>

                {kind === 'TRAINING' && (
                  <div>
                    <p className="mb-2 text-base text-body">Кого обучали (обязательно)</p>
                    <div className="flex flex-col gap-touch">
                      {people
                        .filter((person) => person.isActive)
                        .map((person) => (
                          <button
                            key={person.id}
                            type="button"
                            onClick={() => patch(kind, { traineeUserId: person.id })}
                            className={`min-h-touch rounded-xl px-4 py-3 text-left text-base font-semibold ${
                              incident.traineeUserId === person.id
                                ? 'bg-sky-700 text-white'
                                : 'bg-muted'
                            }`}
                          >
                            {person.fullName}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}

      <label className="flex flex-col gap-2 rounded-xl bg-card shadow-card p-4 text-base">
        Комментарий по смене
        <textarea
          rows={3}
          value={draft.comment ?? ''}
          onChange={(event) => onChange({ comment: event.target.value || null })}
          className="rounded-xl bg-canvas px-4 py-3 text-lg outline-none focus:ring-2 focus:ring-sky-500"
        />
      </label>
    </div>
  );
}
